import React, { useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  Box,
  Button,
  Stack,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchTrainingVideos,
  markTrainingVideoComplete,
} from "../../../store/Auth/authThunks";

export default function TrainingVideoGate({ onComplete }) {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.auth.user);
  const videoRef = useRef(null);

  const [videoQueue, setVideoQueue] = useState([]); // ✅ always an array
  const [currentIndex, setCurrentIndex] = useState(0);
  const [watchedIndexes, setWatchedIndexes] = useState(new Set());
  const [showNextButton, setShowNextButton] = useState(false);

  useEffect(() => {
    if (!user) return;

    dispatch(fetchTrainingVideos())
      .unwrap()
      .then((videos) => {
        setVideoQueue(Array.isArray(videos) ? videos : []); // ✅ ensure array
      })
      .catch(() => {
        setVideoQueue([]); // fallback if API fails
      });
  }, [user, dispatch]);

  useEffect(() => {
    if (videoRef.current && currentIndex > 0) {
      videoRef.current.play().catch(() => {});
    }
  }, [currentIndex]);

  const handleVideoEnd = () => {
    setWatchedIndexes((prev) => new Set(prev).add(currentIndex));
    setShowNextButton(true);
  };

  const handlePlayNext = () => {
    setCurrentIndex((prev) => prev + 1);
    setShowNextButton(false);
  };

  const handlePrev = () => {
    if (currentIndex > 0) {
      setCurrentIndex((prev) => prev - 1);
      setShowNextButton(false);
    }
  };

  const handleAcknowledge = async () => {
    await dispatch(markTrainingVideoComplete());
    onComplete();
  };

  // console.log("videoQueue:", videoQueue);
  // console.log("currentIndex:", currentIndex);
  // console.log("currentVideo:", currentVideo);

  const currentVideo = videoQueue?.[currentIndex];
  const allVideosWatched =
    videoQueue?.length > 0 && watchedIndexes.size >= videoQueue.length;

  if (!Array.isArray(videoQueue) || !videoQueue.length || !currentVideo) {
    return (
      <Dialog open maxWidth="md" fullWidth>
        <DialogTitle>
          <Typography fontWeight="bold">
            Preparing training videos...
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography align="center">Loading...</Typography>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open maxWidth="md" fullWidth>
      <DialogTitle>
        <Typography fontWeight="bold">
          Before you can proceed with accessing the site, you must watch the
          below training videos.
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Box display="flex" justifyContent="center" mb={2} position="relative">
          <video
            ref={videoRef}
            key={`video-${currentIndex}-${currentVideo.filename}`}
            width="720"
            height="405"
            controls
            onEnded={handleVideoEnd}
            style={{ borderRadius: "12px" }}
          >
            <source src={currentVideo.url} type="video/mp4" />
            Your browser does not support the video tag.
          </video>

          {showNextButton && currentIndex + 1 < videoQueue.length && (
            <Box
              position="absolute"
              top="50%"
              left="50%"
              sx={{
                transform: "translate(-50%, -50%)",
                backgroundColor: "rgba(0, 0, 0, 0.7)",
                padding: 4,
                borderRadius: 2,
              }}
            >
              <Typography color="white" mb={2}>
                This video has ended.
              </Typography>
              <Button variant="contained" onClick={handlePlayNext}>
                Play Next Video
              </Button>
            </Box>
          )}
        </Box>

        <Stack
          direction="row"
          spacing={2}
          justifyContent="center"
          alignItems="center"
          mt={2}
        >
          <Button
            variant="outlined"
            disabled={currentIndex === 0}
            onClick={handlePrev}
          >
            Previous
          </Button>

          <Typography variant="body2" color="text.secondary">
            Video {currentIndex + 1} of {videoQueue.length}
          </Typography>

          <Button
            variant="contained"
            onClick={handleAcknowledge}
            disabled={!allVideosWatched}
          >
            Acknowledge & Continue
          </Button>
        </Stack>

        <Typography align="center" mt={2} color="text.secondary">
          {allVideosWatched
            ? "✅ All training videos completed"
            : "Please watch all videos to continue"}
        </Typography>
      </DialogContent>
    </Dialog>
  );
}
